/**
 * Recipients module API client — mode-aware fetchApi wrapper.
 *
 * Phase 5 of demo-tenant architecture (2026-05-06):
 * Demo and production both hit the real BE at /v1/recipients/...; the
 * difference is which (token, workspaceId) pair fetchApi attaches based
 * on `useApiModeStore.mode`. Demo mutations now succeed against the
 * per-session demo tenant (mounted by /v1/auth/demo-bootstrap) instead
 * of throwing DemoModeReadOnlyError.
 *
 * The legacy /api/recipients/<path> Next.js mock routes are dead and
 * scheduled for deletion in Phase 6.
 *
 * All recipient-domain backend controllers are nested under /recipients/*
 * (including /recipients/groups, /recipients/tags, /recipients/custom-fields,
 * /recipients/suppression, /recipients/staging, /recipients/recycle-bin,
 * /recipients/import, /recipients/export, /recipients/sync, /recipients/audit,
 * /recipients/settings, /recipients/data-quality, /recipients/org-chart) so
 * this single wrapper covers all 14 recipient-domain sub-controllers.
 */
import { fetchApi, ApiClientError } from '@/lib/api/client';

export { ApiClientError as ApiError };

export async function recipientsFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  // Author passes the path AFTER /recipients/ — wrapper enforces the prefix
  // so consumers cannot accidentally encode /v1/, /api/, or omit the module
  // prefix.
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const endpoint = `/recipients/${cleanPath}`;
  return fetchApi<T>(endpoint, options as RequestInit & { skipAuth?: boolean });
}

// Backwards-compat alias — existing callers keep working without changes.
export const recipientsApi = recipientsFetch;
